import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { NIL as NIL_UUID } from "uuid";
import CompanyHeading from "../../../components/theming/CompanyHeading";
import { CheckoutButtons } from "../../../components/core/booking/checkout/checkoutButtons/CheckoutButtons";
import { BookingDataWithPayment, BookingEventData } from "../../../models/bookingData";
import { AppState } from "../../../redux";
import { bookEvent } from "../../../redux/bookings/actions";
import { metaDataService } from "../../../api";
import i18n from "i18next";
import DOMPurify from "dompurify";
import { Utils } from "../../../services/utils";
import CircularProgress from "@material-ui/core/CircularProgress";

/**
 * Prepayment checkout page for submitting bookings and showing bank transfer info.
 */
export const PrepaymentPage: React.FC = () => {
  const { t } = useTranslation(["booking", "common", "snackbars"]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const cartState = useSelector((appState: AppState) => appState.cart);
  const bookingState = useSelector((appState: AppState) => appState.booking);
  const billingAddressState = useSelector(
    (appState: AppState) => appState.billingAddress
  );
  const orderState = useSelector((appState: AppState) => appState.order);
  const [prepaymentConditionsDe, setPrepaymentConditionsDe] = useState("");
  const [prepaymentConditionsEn, setPrepaymentConditionsEn] = useState("");
  const [metaDataIsLoading, setMetaDataIsLoading] = useState(true);

  /**
   * Build booking payload from cart state and trigger the booking action.
   */
  const handleSubmit = () => {
    const events: BookingEventData[] = cartState.cart.cart_items.map((cartItem) => {
      return {
        event: cartItem.id,
        status: 10,
        catering_option: cartItem.catering_option,
        use_alternative_billing_address: true,
      };
    });

    const order = orderState.orderData?.id
      ? orderState.orderData
      : billingAddressState.payment.order || {
          id: NIL_UUID,
          orderNumber: 0,
          orderSum: -1,
          events: [],
          items: [],
        };

    const bookingDataWithPayment: BookingDataWithPayment = {
      events,
      payment: {
        order: order,
        payment_method:
          billingAddressState.payment.payment_method || "Vorkasse per Banküberweisung",
        invoice_organization: {
          id: NIL_UUID,
        },
        invoice_is_for_user: true,
        paypal_data: {
          id: "",
          transaction_id: "",
          gross_amount: "",
          capture_id: "",
          currency_code: "",
        },
      },
    };

    const book_for = cartState.cart.cart_items.find((item) => !!item.book_for?.id);
    if (book_for) {
      bookingDataWithPayment.book_for = book_for.book_for.id;
    }

    if (events.length > 0) {
      dispatch(bookEvent(bookingDataWithPayment));
    } else {
      enqueueSnackbar(t("booking.emptyCart", { ns: "snackbars" }), {
        variant: "error",
      });
    }
  };

  /**
   * Fetch metadata from the API and set the prepayment conditions content.
   * */
  useEffect(() => {
    metaDataService
      .getMetaData()
      .then((metaData) => {
        setPrepaymentConditionsDe(metaData?.prepayment_conditions_german);
        setPrepaymentConditionsEn(metaData?.prepayment_conditions_english);
        setMetaDataIsLoading(false);
      })
      .catch(() => {
        setMetaDataIsLoading(false);
      });
  }, []);

  const prepaymentConditions =
    i18n.language === "en" ? prepaymentConditionsEn : prepaymentConditionsDe;

  const sanitizedPrepaymentConditions = DOMPurify.sanitize(
    Utils.convertRteStateToHtml(Utils.convertJsonToRteState(prepaymentConditions))
  );

  return metaDataIsLoading ? (
    <CircularProgress />
  ) : (
    <>
      <CompanyHeading>{t("prepayment.title", { ns: "booking" })}</CompanyHeading>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Grid container spacing={3} className="grid">
          <Grid item xs={12} lg={12}>
            <Typography
              dangerouslySetInnerHTML={{
                __html: sanitizedPrepaymentConditions,
              }}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Typography>
              {t("prepayment.noticePaymentObligation", { ns: "booking" })}
            </Typography>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Typography>
              {t("prepayment.noticeTransferDetails", { ns: "booking" })}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CheckoutButtons isPaypal={false} isLoading={bookingState.isLoading} />
        </Grid>
      </form>
    </>
  );
};
